/**
 * Persist the exchanged Matrix credentials for the current tab so a page
 * refresh can restore the session WITHOUT re-exchanging the one-time `?s=`
 * token (which is single-use and would fail the second time).
 *
 * sessionStorage (not localStorage) is deliberate: it survives refresh but is
 * scoped per-tab, so two demo windows (e.g. Rana + Mark) never share a session,
 * and nothing lingers after the tab closes.
 */
import type { SessionExchangeData } from "../../api/types";

const KEY = "cmc.session.v1";

export interface StoredSession {
  creds: SessionExchangeData;
  /** The one-time token that produced these creds (to detect a NEW link). */
  sourceToken: string;
}

export function loadStoredSession(): StoredSession | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredSession;
    const c = parsed?.creds;
    if (!c || !c.accessToken || !c.userId || !c.homeserverUrl) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function saveStoredSession(value: StoredSession): void {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(value));
  } catch {
    // Storage may be unavailable (private mode / blocked in iframe); the
    // session still works in-memory for this page load.
  }
}

export function clearStoredSession(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // ignore
  }
}
